import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ready | saving | success | error
  const [formError, setFormError] = useState('');
  const [exchangeError, setExchangeError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const { hash, search, origin, pathname } = window.location;
    const hashParams = new URLSearchParams(hash?.replace(/^#/, ''));
    const queryParams = new URLSearchParams(search);

    const urlError = queryParams?.get('error_description') || hashParams?.get('error_description');
    if (urlError) {
      setExchangeError(decodeURIComponent(urlError));
      setStatus('error');
      return;
    }

    const type = queryParams?.get('type') || hashParams?.get('type');
    if (type !== 'recovery') {
      setExchangeError('El enlace de recuperación es inválido o ha expirado. Solicita uno nuevo desde la pantalla de inicio de sesión.');
      setStatus('error');
      return;
    }

    const exchangeSession = async () => {
      try {
        let exchangeErrorResult = null;

        if (queryParams?.get('code')) {
          const code = queryParams?.get('code');
          const { error } = await supabase?.auth?.exchangeCodeForSession(code);
          exchangeErrorResult = error;
        } else if (hashParams?.get('access_token') && hashParams?.get('refresh_token')) {
          const access_token = hashParams?.get('access_token');
          const refresh_token = hashParams?.get('refresh_token');

          const { error } = await supabase?.auth?.setSession({
            access_token,
            refresh_token
          });
          exchangeErrorResult = error;
        } else {
          exchangeErrorResult = { message: 'El enlace de recuperación está incompleto. Solicita uno nuevo.' };
        }

        if (exchangeErrorResult) {
          setExchangeError(exchangeErrorResult?.message || 'El enlace ya no es válido. Solicita otro correo de recuperación.');
          setStatus('error');
          return;
        }

        // limpiar tokens de la URL
        window.history.replaceState({}, '', `${origin}${pathname}`);
        setStatus('ready');
      } catch (error) {
        setExchangeError('No pudimos validar tu enlace. Intenta nuevamente desde el correo o solicita uno nuevo.');
        setStatus('error');
      }
    };

    exchangeSession();
  }, []);

  const handleSubmit = async (event) => {
    event?.preventDefault();
    setFormError('');

    if (!password || password?.length < 6) {
      setFormError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Las contraseñas no coinciden.');
      return;
    }

    setStatus('saving');

    try {
      const { error } = await supabase?.auth?.updateUser({ password });
      if (error) {
        setFormError(error?.message || 'No pudimos actualizar tu contraseña. Intenta de nuevo.');
        setStatus('ready');
        return;
      }

      setStatus('success');
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
    } catch (error) {
      setFormError('Ocurrió un problema al actualizar tu contraseña. Intenta nuevamente.');
      setStatus('ready');
    }
  };

  const renderContent = () => {
    if (status === 'loading' || status === 'saving') {
      return (
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Icon name="Loader2" size={24} className="animate-spin text-primary" />
          </div>
          <p className="text-sm text-text-secondary">
            {status === 'loading' ? 'Validando enlace de recuperación…' : 'Actualizando tu contraseña…'}
          </p>
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className="space-y-6 text-center">
          <div className="flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-error/10 dark:bg-error/20">
              <Icon name="AlertTriangle" size={28} className="text-error" />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-text-primary">Enlace inválido o expirado</h1>
            <p className="text-sm text-text-secondary">
              {exchangeError || 'Solicita un nuevo correo de recuperación desde la pantalla de inicio de sesión.'}
            </p>
          </div>
          <Button onClick={() => navigate('/login')} variant="default">
            Volver al inicio de sesión
          </Button>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="space-y-6 text-center">
          <div className="flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10 dark:bg-success/20">
              <Icon name="CheckCircle" size={28} className="text-success" />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-text-primary">Contraseña actualizada</h1>
            <p className="text-sm text-text-secondary">
              Tu contraseña se actualizó correctamente. Te redirigiremos al inicio de sesión en un momento.
            </p>
          </div>
          <Button onClick={() => navigate('/login')} variant="default">
            Ir al inicio de sesión ahora
          </Button>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold text-text-primary">Crear nueva contraseña</h1>
          <p className="text-sm text-text-secondary">
            Ingresa y confirma tu nueva contraseña para continuar.
          </p>
        </div>

        <Input
          label="Nueva contraseña"
          type="password"
          name="password"
          placeholder="Ingresa tu nueva contraseña"
          value={password}
          onChange={(event) => setPassword(event?.target?.value)}
          required
          minLength={6}
        />

        <Input
          label="Confirmar contraseña"
          type="password"
          name="confirmPassword"
          placeholder="Vuelve a ingresar la contraseña"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event?.target?.value)}
          required
          minLength={6}
        />

        {formError && (
          <div className="p-3 bg-error/10 dark:bg-error/20 border border-error/20 dark:border-error/40 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <span className="text-sm text-error">{formError}</span>
            </div>
          </div>
        )}

        <Button type="submit" variant="default" fullWidth>
          Guardar nueva contraseña
        </Button>
        <Button type="button" variant="ghost" fullWidth onClick={() => navigate('/login')}>
          Cancelar y volver al inicio de sesión
        </Button>
      </form>
    );
  };

  return (
    <>
      <Helmet>
        <title>Restablecer contraseña - MercadoLibre Bulk Publisher</title>
      </Helmet>
      <div
        className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8"
        style={{
          backgroundImage: "url('/assets/images/login-background.avif')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-transparent dark:from-black/70 dark:via-black/60 dark:to-black/70" aria-hidden="true" />
        <div className="relative w-full max-w-lg">
          <div className="rounded-3xl border border-white/40 dark:border-white/10 bg-white dark:bg-[#0f172ae6] shadow-elevated p-8 space-y-6 transition-smooth">
            <div className="flex items-center justify-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary">
                <Icon name="Package" size={24} color="white" />
              </div>
              <div className="text-left">
                <h2 className="text-lg font-semibold text-text-primary">MercadoLibre Bulk Publisher</h2>
                <p className="text-xs text-text-secondary">Restablecer contraseña</p>
              </div>
            </div>
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
